import { useMemo } from 'react'
import { collection, orderBy, query, where } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { COLLECTIONS, type Company, type Offer, type Redemption } from '@shared/model'
import { groupOffersByCompany } from '@shared/offers'
import { useAuth } from '@/lib/auth'
import { useQuerySnapshot } from './useQuerySnapshot'

/** Erbjudandena i en period. Skickar man null får man tom lista. */
export function useOffers(bookletId: string | null, includeInactive = false) {
  const offersQuery = useMemo(() => {
    if (!bookletId) return null
    const base = collection(db, COLLECTIONS.offers)
    return includeInactive
      ? query(base, where('bookletId', '==', bookletId), orderBy('sortOrder'))
      : query(
          base,
          where('bookletId', '==', bookletId),
          where('active', '==', true),
          orderBy('sortOrder'),
        )
  }, [bookletId, includeInactive])

  const result = useQuerySnapshot<Offer>(offersQuery)

  // Ett företags erbjudanden står tillsammans, se groupOffersByCompany.
  const data = useMemo(() => groupOffersByCompany(result.data), [result.data])

  return { ...result, data }
}

export function useCompanies(includeInactive = false) {
  const companiesQuery = useMemo(() => {
    const base = collection(db, COLLECTIONS.companies)
    return includeInactive
      ? query(base, orderBy('sortOrder'))
      : query(base, where('active', '==', true), orderBy('sortOrder'))
  }, [includeInactive])

  return useQuerySnapshot<Company>(companiesQuery)
}

/** Användarens egna inlösen i en period – styr hur många uttag som är kvar. */
export function useMyRedemptions(bookletId: string | null) {
  const { user } = useAuth()

  const redemptionsQuery = useMemo(() => {
    if (!user || !bookletId) return null
    return query(
      collection(db, COLLECTIONS.redemptions),
      where('userUid', '==', user.uid),
      where('bookletId', '==', bookletId),
    )
  }, [user, bookletId])

  const result = useQuerySnapshot<Redemption>(redemptionsQuery)

  /** Antal giltiga (ej återställda) inlösen per erbjudande. */
  const usedByOffer = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const redemption of result.data) {
      if (redemption.revoked) continue
      counts[redemption.offerId] = (counts[redemption.offerId] ?? 0) + 1
    }
    return counts
  }, [result.data])

  const totalSavedSek = useMemo(
    () =>
      result.data.reduce(
        (sum, redemption) => (redemption.revoked ? sum : sum + redemption.valueSek),
        0,
      ),
    [result.data],
  )

  return { ...result, usedByOffer, totalSavedSek }
}
